/**
 * Add-to-cart quantity guard.
 *
 * Validates the quantity before any add-to-cart click is handled — including
 * buttons that bypass the theme's own form submission. The AOV bundle app, for
 * example, converts the theme's [name="add"] button to type="button" and adds
 * the items itself, which skips both native validation and ProductForm's
 * submit handler. A document-level capture listener runs before those handlers,
 * so we can block the action and show the browser's native validation tooltip
 * — the same standard already used on regular products.
 */
if (!window.quantityGuardAttached) {
  window.quantityGuardAttached = true;

  document.addEventListener('click', (evt) => {
    const addBtn = evt.target.closest('[name="add"]');
    if (!addBtn) return;

    // Limit scope to the form/component the button belongs to so we read the
    // correct quantity field (and error container) on pages with several forms.
    const scope = addBtn.closest('product-form, form') || document;
    const qtyInput = scope.querySelector('input[name="quantity"]');
    if (!qtyInput) return;

    const minAttr = parseInt(qtyInput.min, 10);
    const min = Number.isNaN(minAttr) ? 1 : minAttr;
    const value = parseInt(qtyInput.value, 10);

    if (Number.isNaN(value) || value < min) {
      // Stop both the native submit and any app-attached click handlers.
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Show the browser's native validation tooltip — the same standard
      // already used on regular products.
      qtyInput.focus();
      qtyInput.reportValidity();
    }
  }, true);
}

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      let quantity = formData.get('quantity');
      let environmentalFeeId = formData.get('environmental-fee-id');

      // Remove fee from formData of main product to add fee as a separate line item
      formData.delete('environmental-fee-id');

      const fetchRequestOpts = this.fetchRequestOpts(formData);

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);
        if (!response.ok) throw new Error(response.status);

        // Add environmental fee as a separate request if present
        if (environmentalFeeId) {
          const feeFormData = new FormData();
          feeFormData.append('id', environmentalFeeId);
          feeFormData.append('quantity', quantity);
          feeFormData.append('sections_url', window.location.pathname);
          feeFormData.append('sections', sections);

          // prepare and send fetch request to add fee to cart
          const fetchfeeRequestOpts = this.fetchRequestOpts(feeFormData);
          const feeResponse = await fetch(theme.routes.cartAdd, fetchfeeRequestOpts);

          if (!feeResponse.ok) {
            const feeData = await feeResponse.json();
            console.warn('fee could not be added:', feeData);
          } else {
            const feeData = await feeResponse.json();
            data.sections = feeData.sections;
          }
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          await ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(async () => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Returns fetch options for adding to cart, including method, headers, and body.
     * @param {FormData} formData - Form data to send in the request body.
     * @returns {object} Fetch options.
     */
    fetchRequestOpts(formData) {
      return {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static async updateCartIcon(response) {
      /* const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      } */

      const freshCartResponse = await fetch('/cart.js');
      if (freshCartResponse.ok) {
        const freshCartData = await freshCartResponse.json();
        updateCartIconCount(freshCartData);
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
